import { http } from '@/shared/api'

import { createRespondedJobPoller } from './respondedJobPoller'

export type RespondedJobState = 'waiting' | 'in_progress' | 'done'

/** One background job, already flattened out of its envelope. */
export interface RespondedJob {
  id: string
  state: RespondedJobState
  error: string | null
  args: Record<string, unknown>
  result: Record<string, unknown> | null
}

/**
 * A backend job polled to completion: created here, then asked every two seconds until it is
 * done or five minutes have passed, surfacing the error sentence it answers with.
 *
 * A job type the server does not allow is refused, so a new one needs a change there first.
 *
 * Two entry points, because a job starts in two ways: `run(type, args)` creates one, while
 * `poll(jid)` picks up a job another endpoint has already started and answered with
 * `meta.jid`. `onError` receives the backend's own sentence — show it:
 * `onError: (msg) => message.error(msg || '<fallback>')`.
 */
export function useRespondedJob() {
  return createRespondedJobPoller<RespondedJob>({
    create: async (type, args) => {
      const response = await http.post<{
        data: { success: boolean }
        meta?: { jid?: number }
      }>('/responded_jobs', { type, args })

      const jid = response.data.meta?.jid
      if (!jid) throw new Error('Job was accepted but returned no jid')

      return jid
    },
    fetch: async (id) => {
      const response = await http.get<{ data: RespondedJob }>(
        `/responded_jobs/${id}`
      )
      const job = response.data.data

      return {
        id: job.id,
        done: job.state === 'done',
        error: job.error,
        args: job.args,
        job
      }
    }
  })
}
